import React, { useState } from "react";
import { toast } from "sonner";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Trash2 } from "lucide-react";
import { api, fmtErr } from "@/lib/api";

/**
 * Reusable delete button with confirmation.
 * Props: url (e.g. /customers/abc), title, description (JSX), onDeleted(callback), size, testid, iconOnly
 */
export default function DeleteConfirm({ url, title, description, onDeleted, size = "sm", testid, iconOnly = false, disabled = false, disabledReason = "" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setBusy(true);
    try {
      await api.delete(url);
      toast.success("Deleted");
      setOpen(false);
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(fmtErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button size={size} variant="outline" onClick={() => setOpen(true)} disabled={disabled}
        className="text-rose-600 hover:text-rose-700 hover:bg-rose-50 border-rose-200"
        data-testid={testid} title={disabled ? disabledReason : "Delete"}>
        <Trash2 className="h-3.5 w-3.5" />{!iconOnly && <span className="ml-1">Delete</span>}
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid={`${testid}-dialog`}>
          <AlertDialogHeader>
            <AlertDialogTitle>{title || "Confirm deletion"}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600 space-y-2">
                {description}
                <div className="text-xs text-rose-600 font-medium">This action cannot be undone.</div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={run} disabled={busy}
              className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600"
              data-testid={`${testid}-confirm`}>
              {busy ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
